/**
 * Pays membres de la CEDEAO - source unique (15 États).
 *
 * Chaque entrée expose :
 *   - code  : ISO 3166-1 alpha-3 (ex. 'TGO') - utilisé par la plupart des endpoints
 *   - code2 : ISO 3166-1 alpha-2 (ex. 'TG')  - utilisé par l'émission digitale
 *   - nom   : libellé dans la langue de l'interface, traduit à la lecture
 *             (referentiels:paysCedeao)
 *   - flag  : drapeau (emoji)
 */
import { avecLibelleTraduit } from '../i18n/libelles';
export const PAYS_CEDEAO = avecLibelleTraduit([
  { code: 'TGO', code2: 'TG', flag: '🇹🇬' },
  { code: 'BEN', code2: 'BJ', flag: '🇧🇯' },
  { code: 'BFA', code2: 'BF', flag: '🇧🇫' },
  { code: 'CPV', code2: 'CV', flag: '🇨🇻' },
  { code: 'CIV', code2: 'CI', flag: '🇨🇮' },
  { code: 'GMB', code2: 'GM', flag: '🇬🇲' },
  { code: 'GHA', code2: 'GH', flag: '🇬🇭' },
  { code: 'GIN', code2: 'GN', flag: '🇬🇳' },
  { code: 'GNB', code2: 'GW', flag: '🇬🇼' },
  { code: 'LBR', code2: 'LR', flag: '🇱🇷' },
  { code: 'MLI', code2: 'ML', flag: '🇲🇱' },
  { code: 'NER', code2: 'NE', flag: '🇳🇪' },
  { code: 'NGA', code2: 'NG', flag: '🇳🇬' },
  { code: 'SEN', code2: 'SN', flag: '🇸🇳' },
  { code: 'SLE', code2: 'SL', flag: '🇸🇱' },
], 'referentiels:paysCedeao', { cle: 'code', champ: 'nom' });

// Index par code ISO3 et ISO2 pour les recherches rapides.
const _index = {};
PAYS_CEDEAO.forEach((p) => { _index[p.code] = p; _index[p.code2] = p; });

/** Retrouve un pays par son code ISO3 ou ISO2. */
export const trouverPays = (code) => _index[code] || null;

/** Drapeau d'un pays (chaîne vide si inconnu). */
export const drapeauPays = (code) => _index[code]?.flag || '';

/** Libellé « 🇹🇬 Togo » (retourne le code brut si inconnu). */
export const libellePays = (code) => {
  const p = _index[code];
  return p ? `${p.flag} ${p.nom}` : (code || '');
};

/**
 * Options prêtes pour un AntD Select : { value, label: '🇹🇬 Togo' }.
 * @param {boolean} useCode2 - utiliser le code ISO2 comme value (défaut : ISO3).
 */
export const optionsPaysCedeao = (useCode2 = false) =>
  PAYS_CEDEAO.map((p) => ({ value: useCode2 ? p.code2 : p.code, label: `${p.flag} ${p.nom}` }));

/**
 * Pays d'un organisme, résolu depuis l'UN OU L'AUTRE de ses deux codes.
 *
 * `organisme` porte `codePays` (ISO-3, ex. « BFA ») ET `codePaysBCB` (code Carte
 * Brune, alpha-2, ex. « BF »), et selon la fiche l'un des deux peut être vide.
 * Ne regarder qu'un seul champ rate donc toutes les lignes qui ne renseignent
 * que l'autre - c'est ce qui faussait le rattachement des compagnies à leur
 * bureau national.
 */
export const paysOrganisme = (o) =>
  trouverPays(o?.codePays) || trouverPays(o?.codePaysBCB) || null;

/**
 * Libellé « 🇧🇫 Burkina Faso » d'un organisme.
 *
 * Un pays hors CEDEAO retombe sur son code brut : afficher « NG » vaut mieux que
 * n'afficher rien, puisque c'est justement le pays qui distingue deux compagnies
 * de même nom.
 */
export const libellePaysOrganisme = (o) => {
  const p = paysOrganisme(o);
  if (p) return `${p.flag} ${p.nom}`;
  return o?.codePays || o?.codePaysBCB || '';
};
